import React, { forwardRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import {
  format,
  getMonth,
  getYear,
  isBefore,
  isSameDay,
  isValid,
  parse,
  setMonth,
  setYear,
  startOfDay,
} from "date-fns";
import { Box, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import "react-datepicker/dist/react-datepicker.css";
import "./dueDatePicker.css";
import { AH_ACCENT, AH_INPUT_BORDER, AH_MUTED, AH_TEXT, AH_WHITE } from "./theme";

/** Matches `.audit-hub-due-date-calendar` width in dueDatePicker.css */
export const DUE_DATE_CALENDAR_WIDTH = 280;

/** Calendar decades: 2040–2049, 2050–2059, … (not the library’s arbitrary 12-year pages). */
const DECADE_SIZE = 10;

const getDecadeStart = (year) => Math.floor(year / DECADE_SIZE) * DECADE_SIZE;

const toDate = (value) => {
  if (!value) return null;
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
};

const headerNavBtnSx = {
  p: 0.5,
  color: AH_TEXT,
  "&.Mui-disabled": { color: "#cbd5e1" },
};

const headerLabelBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.15,
  border: "1px solid transparent",
  background: "transparent",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 700,
  color: AH_TEXT,
  lineHeight: 1.2,
  px: 0.75,
  py: 0.35,
  borderRadius: "8px",
  transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
  "&:hover": {
    backgroundColor: "#fff4eb",
    borderColor: "#fdddc2",
    color: AH_ACCENT,
  },
  "&:focus-visible": {
    outline: `2px solid ${AH_ACCENT}`,
    outlineOffset: 1,
  },
};

const HeaderJumpButton = ({ ariaLabel, title, onClick, children }) => (
  <Box
    component="button"
    type="button"
    aria-label={ariaLabel}
    title={title}
    onClick={onClick}
    sx={headerLabelBtnSx}
  >
    <Box component="span">{children}</Box>
    <ArrowDropDownIcon sx={{ fontSize: 18, color: "inherit", opacity: 0.85, ml: -0.25 }} aria-hidden />
  </Box>
);

const DueDateInput = forwardRef(function DueDateInput(
  { id, value, onClick, onClear, placeholder, label = "Due Date", ariaLabel = "Due Date", size, disabled, required, error, allowClear = true },
  ref
) {
  return (
    <TextField
      id={id}
      label={label}
      size={size}
      placeholder={placeholder || "mm/dd/yyyy"}
      value={value || ""}
      onClick={onClick}
      inputRef={ref}
      disabled={disabled}
      required={Boolean(required && label)}
      error={error}
      fullWidth
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            {value && !disabled && !required && allowClear ? (
              <IconButton
                size="small"
                aria-label={`Clear ${ariaLabel}`}
                edge="end"
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onClear();
                }}
                sx={{ color: AH_MUTED, mr: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
            <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: AH_MUTED }} />
          </InputAdornment>
        ),
      }}
      inputProps={{ "aria-label": ariaLabel, "aria-required": required || undefined }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          fontSize: "0.875rem",
          minHeight: 40,
          backgroundColor: AH_WHITE,
          cursor: "pointer",
          "& fieldset": { borderColor: AH_INPUT_BORDER },
          "&:hover fieldset": { borderColor: "#cbd5e1" },
          "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
        },
        "& .MuiInputBase-input": {
          cursor: "pointer",
          fontSize: "0.875rem",
          color: AH_TEXT,
        },
        "& .MuiInputBase-input::placeholder": { color: "#94a3b8", opacity: 1 },
        "& .MuiInputLabel-root": { fontSize: "0.875rem" },
      }}
    />
  );
});

/**
 * True calendar-decade year jump (e.g. 2040–2049). Header range always matches the grid.
 */
const DecadeYearView = ({
  date,
  selectedYear,
  onSelectYear,
  onChangeDecade,
  isYearDisabled,
}) => {
  const year = getYear(date);
  const decadeStart = getDecadeStart(year);
  const decadeEnd = decadeStart + DECADE_SIZE - 1;
  const years = Array.from({ length: DECADE_SIZE }, (_, index) => decadeStart + index);

  return (
    <Box className="audit-hub-due-date-decade" sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 0.5,
          pt: 0.5,
          pb: 0.25,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <IconButton
          size="small"
          aria-label="Previous decade"
          onClick={() => onChangeDecade(decadeStart - DECADE_SIZE)}
          sx={headerNavBtnSx}
        >
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT }}>
          {decadeStart} – {decadeEnd}
        </Typography>
        <IconButton
          size="small"
          aria-label="Next decade"
          onClick={() => onChangeDecade(decadeStart + DECADE_SIZE)}
          sx={headerNavBtnSx}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box
        role="listbox"
        aria-label={`Years ${decadeStart} to ${decadeEnd}`}
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 0.75,
          p: 1,
        }}
      >
        {years.map((y) => {
          const disabled = Boolean(isYearDisabled?.(y));
          const selected = selectedYear === y;
          return (
            <Box
              key={y}
              component="button"
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={String(y)}
              disabled={disabled}
              onClick={() => {
                if (!disabled) onSelectYear(y);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 40,
                m: 0,
                p: 0,
                border: "none",
                borderRadius: "8px",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: selected ? 700 : 600,
                cursor: disabled ? "not-allowed" : "pointer",
                color: disabled ? "#cbd5e1" : selected ? AH_WHITE : AH_TEXT,
                backgroundColor: selected ? AH_ACCENT : "transparent",
                "&:hover": disabled
                  ? { backgroundColor: "transparent" }
                  : selected
                    ? { backgroundColor: AH_ACCENT }
                    : { backgroundColor: "#f3f4f6" },
              }}
            >
              {y}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Day → click month for 12-month grid; click year for decade grid.
 * Matches long-horizon contract date navigation (PPA / Customer / Task Order).
 */
const CalendarHeader = ({
  date,
  view,
  onViewChange,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => {
  const year = getYear(date);

  if (view === "month") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5, pt: 0.5, pb: 0.25 }}>
        <IconButton
          size="small"
          aria-label="Previous year"
          onClick={() => changeYear(year - 1)}
          sx={headerNavBtnSx}
        >
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <HeaderJumpButton
          ariaLabel="Select year"
          title="Click to choose year"
          onClick={() => onViewChange("year")}
        >
          {year}
        </HeaderJumpButton>
        <IconButton
          size="small"
          aria-label="Next year"
          onClick={() => changeYear(year + 1)}
          sx={headerNavBtnSx}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5, pt: 0.5, pb: 0.25 }}>
      <IconButton
        size="small"
        aria-label="Previous month"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        sx={headerNavBtnSx}
      >
        <ChevronLeftIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
        <HeaderJumpButton
          ariaLabel="Select month"
          title="Click to choose month"
          onClick={() => onViewChange("month")}
        >
          {format(date, "MMMM")}
        </HeaderJumpButton>
        <HeaderJumpButton
          ariaLabel="Select year"
          title="Click to choose year"
          onClick={() => onViewChange("year")}
        >
          {format(date, "yyyy")}
        </HeaderJumpButton>
      </Box>
      <IconButton
        size="small"
        aria-label="Next month"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        sx={headerNavBtnSx}
      >
        <ChevronRightIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

const DueDateField = ({
  id,
  value,
  onChange,
  fullWidth = false,
  showLabel = true,
  size,
  maxDate,
  markedDate,
  markedDateLabel = "Assessment due date",
  disabled = false,
  portalId,
  required = false,
  error = false,
  helperText = "",
  allowClear = true,
  minDate,
  ariaLabel = "Due Date",
  placeholder = "mm/dd/yyyy",
}) => {
  const marked = toDate(markedDate);
  const selected = toDate(value);
  const [view, setView] = useState("day");
  const [cursor, setCursor] = useState(selected || new Date());

  const resolvedMinDate = minDate === null ? undefined : toDate(minDate) || new Date();
  const resolvedMaxDate = toDate(maxDate) || undefined;

  useEffect(() => {
    if (selected) setCursor(selected);
  }, [value]);

  // Keep the open calendar on/after minDate so month/year pages aren't stranded before the bound.
  useEffect(() => {
    if (minDate === null || minDate === undefined || minDate === "") return;
    const bound = toDate(minDate);
    if (!bound) return;
    setCursor((prev) =>
      isBefore(startOfDay(prev), startOfDay(bound)) ? bound : prev
    );
  }, [minDate]);

  const isMarked = (date) => Boolean(marked) && isSameDay(date, marked);

  const isYearOutOfBounds = (year) => {
    if (resolvedMinDate && year < getYear(resolvedMinDate)) return true;
    if (resolvedMaxDate && year > getYear(resolvedMaxDate)) return true;
    return false;
  };

  const isMonthOutOfBounds = (date) => {
    const y = getYear(date);
    const m = getMonth(date);
    if (resolvedMinDate) {
      const minY = getYear(resolvedMinDate);
      const minM = getMonth(resolvedMinDate);
      if (y < minY || (y === minY && m < minM)) return true;
    }
    if (resolvedMaxDate) {
      const maxY = getYear(resolvedMaxDate);
      const maxM = getMonth(resolvedMaxDate);
      if (y > maxY || (y === maxY && m > maxM)) return true;
    }
    return false;
  };

  const isDayOutOfBounds = (date) => {
    const day = startOfDay(date);
    if (resolvedMinDate && isBefore(day, startOfDay(resolvedMinDate))) return true;
    if (resolvedMaxDate && isBefore(startOfDay(resolvedMaxDate), day)) return true;
    return false;
  };

  const widthSx = fullWidth
    ? { width: "100%", maxWidth: "100%", minWidth: 0 }
    : { width: DUE_DATE_CALENDAR_WIDTH, maxWidth: "100%", minWidth: 0 };

  const handlePickerChange = (date) => {
    if (!date) {
      onChange("");
      return;
    }
    if (view === "year") {
      // Library can still fire year clicks for disabled cells — ignore out-of-range years.
      if (isYearOutOfBounds(getYear(date))) return;
      const next = setYear(cursor, getYear(date));
      setCursor(next);
      setView("month");
      return;
    }
    if (view === "month") {
      if (isMonthOutOfBounds(date)) return;
      const next = setMonth(setYear(cursor, getYear(date)), date.getMonth());
      setCursor(next);
      setView("day");
      return;
    }
    if (isDayOutOfBounds(date)) return;
    onChange(format(date, "yyyy-MM-dd"));
  };

  return (
    <Box
      className="audit-hub-due-date"
      sx={{
        ...widthSx,
        "& .react-datepicker-wrapper, & .react-datepicker__input-container": {
          display: "block",
          width: "100%",
        },
        "& .MuiTextField-root": { width: "100%" },
      }}
    >
      <DatePicker
        id={id}
        selected={selected}
        openToDate={cursor}
        onChange={handlePickerChange}
        onCalendarClose={() => setView("day")}
        minDate={resolvedMinDate}
        maxDate={resolvedMaxDate}
        disabled={disabled}
        portalId={portalId}
        dayClassName={(date) => (isMarked(date) ? "audit-hub-due-date-marked" : undefined)}
        renderDayContents={(day, date) =>
          isMarked(date) ? <span title={markedDateLabel} aria-label={markedDateLabel}>{day}</span> : day
        }
        dateFormat="MM/dd/yyyy"
        placeholderText={placeholder}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        popperProps={{
          middleware: [],
        }}
        popperClassName="audit-hub-due-date-popper"
        calendarClassName="audit-hub-due-date-calendar"
        showMonthYearPicker={view === "month"}
        showYearPicker={false}
        shouldCloseOnSelect={view === "day"}
        calendarContainer={({ className, children }) => (
          <div className={className}>
            {view === "year" ? (
              <DecadeYearView
                date={cursor}
                selectedYear={selected ? getYear(selected) : getYear(cursor)}
                isYearDisabled={isYearOutOfBounds}
                onChangeDecade={(decadeStartYear) => {
                  setCursor((prev) => setYear(prev, decadeStartYear));
                }}
                onSelectYear={(nextYear) => {
                  if (isYearOutOfBounds(nextYear)) return;
                  setCursor((prev) => setYear(prev, nextYear));
                  setView("month");
                }}
              />
            ) : (
              children
            )}
          </div>
        )}
        renderCustomHeader={(props) => (
          <CalendarHeader
            {...props}
            view={view}
            onViewChange={setView}
            changeYear={(nextYear) => {
              props.changeYear(nextYear);
              setCursor((prev) => setYear(prev, nextYear));
            }}
            changeMonth={(nextMonth) => {
              props.changeMonth(nextMonth);
              setCursor((prev) => setMonth(prev, nextMonth));
            }}
          />
        )}
        customInput={
          <DueDateInput
            onClear={() => onChange("")}
            label={showLabel ? "Due Date" : ""}
            ariaLabel={ariaLabel}
            size={size}
            disabled={disabled}
            required={required}
            error={Boolean(error)}
            allowClear={allowClear}
            placeholder={placeholder}
          />
        }
      />
      {helperText ? (
        <Typography sx={{ mt: 0.5, fontSize: "0.75rem", color: "#d32f2f", lineHeight: 1.4 }}>{helperText}</Typography>
      ) : null}
    </Box>
  );
};

export default DueDateField;
